import { useEffect, useState, useCallback, useMemo, useRef } from "react"
import { useParams, Link } from "react-router-dom"
import { api } from "@/lib/api"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { IconArrowLeft, IconDeviceFloppy, IconChevronDown } from "@tabler/icons-react"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { toast } from "sonner"

interface PermissionDto {
  id: number
  code: string
  description: string
}

interface RoleDto {
  id: number
  name: string
  description: string
  permissions: PermissionDto[]
}

interface UserDto {
  id: string
  displayName: string
  userPrincipalName: string
}

interface UserAccessDto {
  assignedRoles: number[]
  assignedDirectPermissions: number[]
}

type RoleCheckState = "checked" | "unchecked" | "indeterminate"

/**
 * When loading from the API, we get a flat list of direct permission IDs.
 * We need to infer which role each one "came from" so we can display them
 * scoped to the correct role card. Uses greedy best-fit: the unassigned role
 * with the highest overlap gets attributed first.
 */
function attributeDirectPerms(
  roles: RoleDto[],
  assignedRoleIds: number[],
  directPermIds: number[]
): Map<number, Set<number>> {
  const remaining = new Set(directPermIds)
  const attribution = new Map<number, Set<number>>()

  const candidates = roles
    .filter((r) => !assignedRoleIds.includes(r.id))
    .map((r) => {
      const permSet = new Set(r.permissions.map((p) => p.id))
      const overlap = [...remaining].filter((pid) => permSet.has(pid)).length
      return { role: r, permSet, overlap }
    })
    .sort((a, b) => b.overlap - a.overlap)

  for (const { role, permSet } of candidates) {
    const attributed = new Set<number>()
    for (const pid of remaining) {
      if (permSet.has(pid)) {
        attributed.add(pid)
      }
    }
    if (attributed.size > 0) {
      attribution.set(role.id, attributed)
      for (const pid of attributed) remaining.delete(pid)
    }
  }

  return attribution
}

export default function AdminUserDetailPage() {
  const { userId } = useParams<{ userId: string }>()
  const [roles, setRoles] = useState<RoleDto[]>([])
  const [user, setUser] = useState<UserDto | null>(null)
  const [userRoles, setUserRoles] = useState<number[]>([])
  // Scoped detachment: maps roleId → set of permIds that remain active after detachment
  const [detachedPerms, setDetachedPerms] = useState<Map<number, Set<number>>>(new Map())
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  // Snapshot of saved state for dirty detection
  const [savedRoles, setSavedRoles] = useState<number[]>([])
  const [savedDirectPerms, setSavedDirectPerms] = useState<number[]>([])

  useEffect(() => {
    async function load() {
      if (!userId) return
      try {
        const [rolesData, accessData, users] = await Promise.all([
          api.get<RoleDto[]>("/api/admin/roles"),
          api.get<UserAccessDto>(`/api/admin/users/${userId}/access`),
          api.get<UserDto[]>("/api/users"),
        ])
        setRoles(rolesData)
        setUserRoles(accessData.assignedRoles)
        setSavedRoles(accessData.assignedRoles)
        setSavedDirectPerms(accessData.assignedDirectPermissions)
        setDetachedPerms(
          attributeDirectPerms(rolesData, accessData.assignedRoles, accessData.assignedDirectPermissions)
        )
        setUser(users.find((u) => u.id === userId) ?? null)
      } catch (err) {
        console.error("Failed to load user access:", err)
        toast.error("Failed to load user data")
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [userId])

  // Flatten detachedPerms back to a flat array for the API
  const flatDirectPerms = useMemo(() => {
    const all = new Set<number>()
    for (const perms of detachedPerms.values()) {
      for (const pid of perms) all.add(pid)
    }
    return Array.from(all)
  }, [detachedPerms])

  const isDirty = useMemo(() => {
    const rolesMatch =
      userRoles.length === savedRoles.length &&
      [...userRoles].sort().every((v, i) => v === [...savedRoles].sort()[i])
    const permsMatch =
      flatDirectPerms.length === savedDirectPerms.length &&
      [...flatDirectPerms].sort((a, b) => a - b).every((v, i) => v === [...savedDirectPerms].sort((a, b) => a - b)[i])
    return !rolesMatch || !permsMatch
  }, [userRoles, flatDirectPerms, savedRoles, savedDirectPerms])

  const getRoleState = useCallback(
    (role: RoleDto): RoleCheckState => {
      if (userRoles.includes(role.id)) {
        return "checked"
      }
      const scoped = detachedPerms.get(role.id)
      if (scoped && scoped.size > 0) {
        return "indeterminate"
      }
      return "unchecked"
    },
    [userRoles, detachedPerms]
  )

  const isPermissionChecked = useCallback(
    (role: RoleDto, permId: number): boolean => {
      if (userRoles.includes(role.id)) {
        return true
      }
      const scoped = detachedPerms.get(role.id)
      return scoped?.has(permId) ?? false
    },
    [userRoles, detachedPerms]
  )

  // Action 1: Toggle a role checkbox
  const toggleRole = useCallback(
    (role: RoleDto) => {
      const state = getRoleState(role)
      if (state === "checked") {
        // Uncheck: remove role entirely, no detached perms
        setUserRoles((prev) => prev.filter((id) => id !== role.id))
      } else {
        // Check (from unchecked or indeterminate): add role, clear any detached perms for it
        setUserRoles((prev) => [...prev, role.id])
        setDetachedPerms((prev) => {
          const next = new Map(prev)
          next.delete(role.id)
          return next
        })
      }
    },
    [getRoleState]
  )

  // Action 2: Toggle an individual permission under a role
  const togglePermission = useCallback(
    (role: RoleDto, permId: number) => {
      const roleIsAssigned = userRoles.includes(role.id)

      if (roleIsAssigned) {
        // Detach: remove the role, add all OTHER perms scoped to this role
        setUserRoles((prev) => prev.filter((id) => id !== role.id))
        const otherPerms = new Set(
          role.permissions.map((p) => p.id).filter((pid) => pid !== permId)
        )
        setDetachedPerms((prev) => {
          const next = new Map(prev)
          next.set(role.id, otherPerms)
          return next
        })
      } else {
        // Toggle within the scoped detached set
        const current = new Set(detachedPerms.get(role.id) ?? [])
        if (current.has(permId)) {
          current.delete(permId)
        } else {
          current.add(permId)
        }
        const allPermIds = new Set(role.permissions.map((p) => p.id))
        if (current.size === allPermIds.size && [...current].every((pid) => allPermIds.has(pid))) {
          // All permissions checked — promote to full role
          setUserRoles((prev) => [...prev, role.id])
          setDetachedPerms((prev) => {
            const next = new Map(prev)
            next.delete(role.id)
            return next
          })
        } else {
          setDetachedPerms((prev) => {
            const next = new Map(prev)
            if (current.size === 0) {
              next.delete(role.id)
            } else {
              next.set(role.id, current)
            }
            return next
          })
        }
      }
    },
    [userRoles, detachedPerms]
  )

  const handleSave = async () => {
    if (!userId) return
    setSaving(true)
    try {
      await api.put(`/api/admin/users/${userId}/access`, {
        assignedRoles: userRoles,
        assignedDirectPermissions: flatDirectPerms,
      })
      setSavedRoles([...userRoles])
      setSavedDirectPerms([...flatDirectPerms])
      toast.success("Access updated successfully")
    } catch (err) {
      console.error("Failed to save:", err)
      toast.error("Failed to save access changes")
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-8 w-64" />
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <div className="flex items-center gap-4">
        <Link to="/admin/users">
          <Button variant="ghost" size="icon">
            <IconArrowLeft className="size-5" />
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {user?.displayName ?? "Unknown User"}
          </h1>
          <p className="text-sm text-muted-foreground">{user?.userPrincipalName}</p>
        </div>
      </div>

      <div className="space-y-4">
        {roles.map((role) => (
          <RoleCard
            key={role.id}
            role={role}
            state={getRoleState(role)}
            isPermissionChecked={(permId) => isPermissionChecked(role, permId)}
            onToggleRole={() => toggleRole(role)}
            onTogglePermission={(permId) => togglePermission(role, permId)}
          />
        ))}
      </div>

      {isDirty && (
        <div className="flex justify-end pt-4">
          <Button onClick={handleSave} disabled={saving}>
            <IconDeviceFloppy className="size-4 mr-2" />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      )}
    </div>
  )
}

function RoleCard({
  role,
  state,
  isPermissionChecked,
  onToggleRole,
  onTogglePermission,
}: {
  role: RoleDto
  state: RoleCheckState
  isPermissionChecked: (permId: number) => boolean
  onToggleRole: () => void
  onTogglePermission: (permId: number) => void
}) {
  const checkboxRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(state === "indeterminate")

  useEffect(() => {
    if (checkboxRef.current) {
      const input = checkboxRef.current
      if (state === "indeterminate") {
        input.dataset.state = "indeterminate"
        input.setAttribute("aria-checked", "mixed")
      }
    }
  }, [state])

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card>
        <CardHeader className={open ? "pb-3" : ""}>
          <div className="flex items-center gap-3">
            <Checkbox
              ref={checkboxRef}
              checked={state === "checked"}
              data-state={state === "indeterminate" ? "indeterminate" : state === "checked" ? "checked" : "unchecked"}
              aria-checked={state === "indeterminate" ? "mixed" : state === "checked"}
              onCheckedChange={() => onToggleRole()}
            />
            <CollapsibleTrigger asChild>
              <button type="button" className="flex items-center gap-2 text-left flex-1 min-w-0">
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-base">{role.name}</CardTitle>
                  <CardDescription>{role.description}</CardDescription>
                </div>
                {state === "indeterminate" && (
                  <Badge variant="outline" className="shrink-0">
                    Custom
                  </Badge>
                )}
                <IconChevronDown
                  className={`size-4 text-muted-foreground shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                />
              </button>
            </CollapsibleTrigger>
          </div>
        </CardHeader>
        <CollapsibleContent>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pl-7">
              {role.permissions.map((perm) => (
                <label
                  key={perm.id}
                  className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 rounded px-2 py-1"
                >
                  <Checkbox
                    checked={isPermissionChecked(perm.id)}
                    onCheckedChange={() => onTogglePermission(perm.id)}
                  />
                  <span className="font-mono text-xs">{perm.code}</span>
                </label>
              ))}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}
